import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { useLookups } from "../context/LookupOptionsContext";
import PartyCombobox from "./PartyCombobox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { branBagCutsForPacking, isPpPacking, RICE_BARDANA_GM_OPTIONS, RICE_BARDANA_BASIS_OPTIONS } from "../lib/salesContractOptions";
import { cn } from "@/lib/utils";

function isBranProduct(product) {
  if (!product) return false;
  const blob = `${product.code || ""} ${product.name || ""}`.toUpperCase();
  return blob.includes("BRAN");
}

function Field({ label, children, className }) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function Section({ title, children, className }) {
  return (
    <section className={cn("min-w-0 space-y-2.5", className)}>
      {title ? (
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </p>
      ) : null}
      {children}
    </section>
  );
}

export default function SalesContractFormFields({
  form,
  setForm,
  buyers = [],
  brokers = [],
  onBuyersChange,
  onBrokersChange,
  readOnly = false,
  contractNumber = null,
  compact = false,
}) {
  const { optionsFor } = useLookups();
  const packingOptions = optionsFor("rice_packing").filter((o) => o.value !== "PP_50KG");
  const paymentOptions = optionsFor("payment_condition");
  const deliveryOptions = optionsFor("sales_delivery_condition");
  const cdPercentOptions = optionsFor("sales_cd_percent");
  const brokerageBasisOptions = optionsFor("brokerage_basis");

  const [crops, setCrops] = useState([]);
  const [riceTypes, setRiceTypes] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, tRes, hRes] = await Promise.all([
          api.get("/production/masters/crops"),
          api.get("/production/masters/rice-types"),
          api.get("/production/masters-hierarchy"),
        ]);
        if (cancelled) return;
        setCrops(cRes.data?.results || cRes.data || []);
        setRiceTypes(tRes.data?.results || tRes.data || []);
        setHierarchy(hRes.data?.results || []);
      } catch (err) {
        toast.error(extractError(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const products = hierarchy;
  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(form.product_id)) || null,
    [products, form.product_id],
  );
  const groups = selectedProduct?.groups || [];
  const selectedGroup = useMemo(
    () => groups.find((g) => String(g.id) === String(form.item_group_id)) || null,
    [groups, form.item_group_id],
  );
  const items = selectedGroup?.items || [];
  const branMode = isBranProduct(selectedProduct) || form.product_family === "RICE_BRAN";

  const setArch = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      if (patch.product_id != null) {
        const prod = products.find((p) => String(p.id) === String(patch.product_id));
        next.product_family = isBranProduct(prod) ? "RICE_BRAN" : "RICE";
        if (!isBranProduct(prod)) {
          next.pricing_mode = "FIXED_PER_QTL";
          next.market_condition = "";
          next.bran_process = "";
        } else {
          if (!next.bran_process) next.bran_process = "PARBOILED";
          Object.assign(next, branBagCutsForPacking(next.packing || "PP_PACKING"));
        }
        if (patch.item_group_id == null) {
          next.item_group_id = "";
          next.item_id = "";
        }
      }
      if (patch.item_group_id != null && patch.item_id == null) {
        next.item_id = "";
      }
      if (patch.item_id != null && patch.item_id !== "") {
        const grp = (prodGroups(products, next.product_id)).find((g) => String(g.id) === String(next.item_group_id));
        const item = (grp?.items || []).find((i) => String(i.id) === String(patch.item_id));
        if (item) next.variety = item.name || item.code || "";
      }
      return next;
    });
  };

  if (readOnly) return null;

  const gap = compact ? "gap-2.5" : "gap-4";
  const stack = compact ? "space-y-3" : "space-y-4";

  return (
    <div className={cn("min-w-0", stack)}>
      <Section title={compact ? "Contract" : null}>
        <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", gap)}>
          {contractNumber ? (
            <Field label="Contract #">
              <Input value={contractNumber} readOnly className="font-mono bg-muted h-9" />
            </Field>
          ) : null}
          <Field label="Contract date">
            <Input
              type="date"
              className="h-9"
              value={form.contract_date || ""}
              onChange={(e) => setForm((f) => ({ ...f, contract_date: e.target.value }))}
            />
          </Field>
          <Field label="Buyer" className={contractNumber ? "sm:col-span-2 lg:col-span-1" : "sm:col-span-2 lg:col-span-1"}>
            <PartyCombobox
              parties={buyers}
              partyId={form.buyer_party_id}
              partyName={form.buyer_name}
              createKind="BUYER"
              placeholder="Search dealer / buyer…"
              testIdPrefix="sales-buyer-combobox"
              onChange={({ partyId, partyName }) => setForm((f) => ({
                ...f,
                buyer_party_id: partyId,
                buyer_name: partyName,
              }))}
              onPartyCreated={(party) => onBuyersChange?.(party)}
            />
          </Field>
        </div>
      </Section>

      <Section title="Product">
        <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", gap)}>
          <Field label="Crop">
            <Select value={form.crop_id ? String(form.crop_id) : ""} onValueChange={(v) => setArch({ crop_id: v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Select crop" /></SelectTrigger>
              <SelectContent>
                {crops.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Rice type">
            <Select value={form.type_id ? String(form.type_id) : ""} onValueChange={(v) => setArch({ type_id: v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Select rice type" /></SelectTrigger>
              <SelectContent>
                {riceTypes.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Product">
            <Select
              value={form.product_id ? String(form.product_id) : ""}
              onValueChange={(v) => setArch({ product_id: v, item_group_id: "", item_id: "" })}
            >
              <SelectTrigger className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>
                {products.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Item group">
            <Select
              value={form.item_group_id ? String(form.item_group_id) : ""}
              onValueChange={(v) => setArch({ item_group_id: v, item_id: "" })}
              disabled={!form.product_id}
            >
              <SelectTrigger className="h-9"><SelectValue placeholder="Select group" /></SelectTrigger>
              <SelectContent>
                {groups.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Item" className="sm:col-span-2 lg:col-span-2">
            <Select
              value={form.item_id ? String(form.item_id) : ""}
              onValueChange={(v) => setArch({ item_id: v })}
              disabled={!form.item_group_id}
            >
              <SelectTrigger className="h-9"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {items.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      {branMode ? (
        <Section title="Bran pricing">
          <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", gap)}>
            <Field label="Bran process">
              <Select
                value={form.bran_process || "PARBOILED"}
                onValueChange={(v) => setForm((f) => ({ ...f, bran_process: v }))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="STEAM">Steam (15 / 16 pt base)</SelectItem>
                  <SelectItem value="PARBOILED">Parboiled / sella (20 pt base)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Pricing">
              <Select
                value={form.pricing_mode || "FIXED_PER_QTL"}
                onValueChange={(v) => setForm((f) => ({ ...f, pricing_mode: v }))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIXED_PER_QTL">Fixed ₹ / qtl</SelectItem>
                  <SelectItem value="PER_OIL_POINT">Per oil point</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.pricing_mode === "PER_OIL_POINT" ? (
              <Field label="Market condition">
                <Select
                  value={form.market_condition || ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, market_condition: v }))}
                >
                  <SelectTrigger className="h-9"><SelectValue placeholder="UP / Haryana" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UP">UP condition</SelectItem>
                    <SelectItem value="HARYANA">Haryana condition</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            ) : null}
          </div>
        </Section>
      ) : null}

      <Section title="Commercial">
        <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", gap)}>
          <Field label="Quantity (qtl)">
            <Input
              type="number"
              min="0"
              step="any"
              className="h-9"
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            />
          </Field>
          <Field label={form.pricing_mode === "PER_OIL_POINT" ? "Rate (₹ / oil point)" : "Rate (per qtl)"}>
            <Input
              type="number"
              min="0"
              step="any"
              className="h-9"
              value={form.rate}
              onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
            />
          </Field>
          <Field label="Packing">
            <Select
              value={form.packing === "PP_50KG" ? "PP_PACKING" : (form.packing || "PP_PACKING")}
              onValueChange={(v) => setForm((f) => {
                const next = { ...f, packing: v };
                if (branMode || f.product_family === "RICE_BRAN") {
                  Object.assign(next, branBagCutsForPacking(v));
                }
                if (!(v === "PP_PACKING" || v === "PP_50KG") || f.product_family === "RICE_BRAN") {
                  next.bardana_gm = "";
                  next.bardana_basis = "PER_BAG";
                }
                return next;
              })}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {packingOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.packing === "BUYER_PACKING" ? (
            <Field label="Buyer packing details" className="sm:col-span-2 lg:col-span-3">
              <Input
                className="h-9"
                value={form.buyer_packing_text || ""}
                onChange={(e) => setForm((f) => ({ ...f, buyer_packing_text: e.target.value }))}
              />
            </Field>
          ) : null}
        </div>

        {branMode && isPpPacking(form.packing) ? (
          <div className="rounded-md border border-amber-300/80 bg-amber-50 px-3 py-2 text-xs text-amber-950">
            <span className="font-medium">PP packing — </span>
            150 gm/bag (0.150 kg) PPE deduction on weighbridge net for rice bran.
          </div>
        ) : null}
        {branMode && form.packing === "JUTE_BAG" ? (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
            <span className="font-medium">Jute bag — </span>
            1.5 kg/bag jute cut on weighbridge net.
          </div>
        ) : null}
        {!branMode && isPpPacking(form.packing) ? (
          <div className={cn("grid grid-cols-1 sm:grid-cols-2 rounded-md border border-border bg-muted/20 p-2.5", gap)}>
            <Field label="Bardana (PP)">
              <Select
                value={form.bardana_gm ? String(form.bardana_gm) : "NONE"}
                onValueChange={(v) => setForm((f) => ({
                  ...f,
                  bardana_gm: v === "NONE" ? "" : v,
                  bardana_basis: f.bardana_basis || "PER_BAG",
                }))}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  {RICE_BARDANA_GM_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Bardana basis">
              <Select
                value={form.bardana_basis || "PER_BAG"}
                onValueChange={(v) => setForm((f) => ({ ...f, bardana_basis: v }))}
                disabled={!form.bardana_gm}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RICE_BARDANA_BASIS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground leading-snug">
                Net = WB net − bardana (gm × bags or × qtl).
              </p>
            </Field>
          </div>
        ) : null}
      </Section>

      <Section title="Terms">
        <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", gap)}>
          <Field label="Payment terms">
            <Select
              value={form.payment_condition || "SAME_DAY"}
              onValueChange={(v) => setForm((f) => ({ ...f, payment_condition: v }))}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {paymentOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Delivery terms">
            <Select
              value={form.delivery_condition || "TRUCK_LOAD"}
              onValueChange={(v) => setForm((f) => ({ ...f, delivery_condition: v }))}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {deliveryOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="min-w-0 flex flex-col justify-end gap-2 pb-0.5">
            <div className="flex items-center gap-2 min-h-9">
              <Checkbox
                id="sc-cd"
                checked={Boolean(form.cd_applicable)}
                onCheckedChange={(v) => setForm((f) => ({ ...f, cd_applicable: Boolean(v) }))}
              />
              <Label htmlFor="sc-cd" className="font-normal text-xs">Cash discount</Label>
              {form.cd_applicable ? (
                <Select value={form.cd_percent || ""} onValueChange={(v) => setForm((f) => ({ ...f, cd_percent: v }))}>
                  <SelectTrigger className="h-8 w-[5.5rem]"><SelectValue placeholder="%" /></SelectTrigger>
                  <SelectContent>
                    {cdPercentOptions.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          </div>
          {form.payment_condition === "OTHER" ? (
            <Field label="Payment terms (other)" className="sm:col-span-2 lg:col-span-3">
              <Input
                className="h-9"
                value={form.payment_condition_other || ""}
                onChange={(e) => setForm((f) => ({ ...f, payment_condition_other: e.target.value }))}
              />
            </Field>
          ) : null}
          {form.delivery_condition === "OTHER" ? (
            <Field label="Delivery terms (other)" className="sm:col-span-2 lg:col-span-3">
              <Input
                className="h-9"
                value={form.delivery_terms_other || ""}
                onChange={(e) => setForm((f) => ({ ...f, delivery_terms_other: e.target.value }))}
              />
            </Field>
          ) : null}
        </div>

        <div className="flex items-center gap-2 pt-0.5">
          <Checkbox
            id="sc-broker"
            checked={Boolean(form.broker_applicable)}
            onCheckedChange={(v) => setForm((f) => ({ ...f, broker_applicable: Boolean(v) }))}
          />
          <Label htmlFor="sc-broker" className="font-normal text-xs">Broker applicable</Label>
        </div>
        {form.broker_applicable ? (
          <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", gap)}>
            <Field label="Broker">
              <PartyCombobox
                parties={brokers}
                partyId={form.broker_party_id}
                partyName={form.broker_name}
                createKind="BROKER"
                placeholder="Search broker…"
                testIdPrefix="sales-broker-combobox"
                onChange={({ partyId, partyName }) => setForm((f) => ({
                  ...f,
                  broker_party_id: partyId,
                  broker_name: partyName,
                }))}
                onPartyCreated={(party) => onBrokersChange?.(party)}
              />
            </Field>
            <Field label="Brokerage basis">
              <Select
                value={form.brokerage_basis || "GROSS_PCT"}
                onValueChange={(v) => setForm((f) => ({ ...f, brokerage_basis: v }))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {brokerageBasisOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={form.brokerage_basis === "PER_QTL" ? "Brokerage (₹/qtl)" : "Brokerage (%)"}>
              <Input
                type="number"
                min="0"
                step="any"
                className="h-9"
                value={form.brokerage}
                onChange={(e) => setForm((f) => ({ ...f, brokerage: e.target.value }))}
              />
            </Field>
          </div>
        ) : null}
      </Section>

      <Field label="Notes">
        <Textarea
          value={form.notes || ""}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          rows={compact ? 2 : 2}
          className="min-h-[2.75rem] resize-y"
        />
      </Field>
    </div>
  );
}

function prodGroups(products, productId) {
  const prod = products.find((p) => String(p.id) === String(productId));
  return prod?.groups || [];
}
